import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react'
import { alpha, Box, Stack, Typography, useTheme } from '@mui/material'

import { useAnnouncementFeed } from 'hooks/api'
import { ROUTES } from 'routes'
import type { IAnnouncement } from 'services/announcementsApi'

/**
 * The two announcements either side of this one, at the foot of `/whats-new/:id`.
 *
 * ## Why this and not the other things that would fill the space
 *
 * The detail page renders a breadcrumb, a title, a chip, a date, a body and a CTA, and
 * then stops — on a three-line announcement that is a third of a screen followed by
 * nothing, which reads as a truncated feed card rather than a page. The question is what
 * genuinely belongs there, not what fits.
 *
 * Kept: **neighbour navigation**. A changelog is read chronologically — the reason to
 * open one entry is usually the reason to read the one before it, and a detail page with
 * no way onward forces a return to the archive and a re-scan to find where you were.
 * Every changelog worth the name has this. It also gives the page a terminal element, so
 * it ENDS rather than stopping.
 *
 * Rejected, deliberately:
 *   - A hero image — filler, and there is no image on an announcement to show.
 *   - "Related announcements of the same kind" — "here are other Fixes" is not a
 *     relation a reader has ever wanted. Kind is a label, not a topic, so this would be
 *     a shelf of unrelated items dressed as relevance.
 *   - A §40 page description — that pattern is for copy that qualifies the whole page,
 *     and an announcement has none: the body IS the content, and a per-announcement
 *     subtitle would have to be invented.
 *   - Restructuring the kind/date header into the page frame — those are the CARD's
 *     content, and moving them out is precisely the drift the variant contract in
 *     `AnnouncementCard` exists to prevent (§36). `variant` changes chrome only.
 *
 * The "You asked for these" references, also named as a candidate, are already on the
 * page — they come from the card, and they became real destinations in the same pass
 * that added this (they had been pointing at a retired board).
 *
 * ## Why it is sometimes absent, and why that is correct
 *
 * Position comes from the FEED, which applies the signup cutoff (`published_at >
 * user.created_at`). So for an announcement published before the reader's account
 * existed — exactly the case a shared link exists for — the current entry is not in the
 * list at all, `findIndex` returns -1, and this renders nothing.
 *
 * That is the honest outcome rather than a gap to paper over. The alternative is a
 * second endpoint returning neighbours without the cutoff, which would hand a new
 * account the eighteen months of history rule 1 in `AnnouncementsService` exists to keep
 * from them — through a side door, and only for whoever followed a link. A missing pager
 * costs a click; that would cost the rule.
 */
export const AnnouncementPager = ({ currentId }: { currentId: string }) => {
  const theme = useTheme()
  const navigate = useNavigate()
  /*
    The referrer is PASSED THROUGH, not minted (§42). The reader came from the archive
    and is still working through it, so the breadcrumb must keep offering the archive as
    they left it. Minting a new one here would make the way back point at the previous
    ANNOUNCEMENT — one they have just finished reading — and every step deeper would
    bury the archive one level further.
  */
  const { state } = useLocation()

  const { data } = useAnnouncementFeed()
  const list = data?.announcements ?? []

  const index = list.findIndex(announcement => announcement.id === currentId)
  if (index === -1) return null

  // The feed is newest-first, so the PREVIOUS index is the newer announcement.
  const newer = index > 0 ? list[index - 1] : null
  const older = index < list.length - 1 ? list[index + 1] : null
  if (!newer && !older) return null

  const tile = (announcement: IAnnouncement, direction: 'newer' | 'older') => {
    const isNewer = direction === 'newer'

    return (
      <Box
        role="link"
        tabIndex={0}
        aria-label={`${isNewer ? 'Newer' : 'Older'}: ${announcement.title}`}
        onClick={() => navigate(ROUTES.WHATS_NEW_ANNOUNCEMENT(announcement.id), { state })}
        onKeyDown={event => {
          if (event.key !== 'Enter' && event.key !== ' ') return
          event.preventDefault()
          navigate(ROUTES.WHATS_NEW_ANNOUNCEMENT(announcement.id), { state })
        }}
        sx={{
          // Older sits in the right-hand column whether or not a newer one exists, so
          // the two directions always occupy the same side of the page.
          gridColumn: isNewer ? 1 : 2,
          p: 4,
          borderRadius: 2,
          cursor: 'pointer',
          outline: 'none',
          bgcolor: 'background.paper',
          /*
            §34's ACTION-ROW pattern, not the selectable-card one: clicking navigates
            and leaves nothing visibly selected, so the brand border is free to mean
            "this is what you are about to activate". Same numbers as the feed card
            this sits beneath, so the two read as one family.

            `:focus-visible` alongside `:hover` is not optional — this is a `div` with
            `role="link"`, so without it the rule is unreachable by keyboard.
          */
          border: `1px solid ${theme.palette.primary[100]}`,
          transition: 'all 0.15s ease-in',
          '&:hover, &:focus-visible': {
            borderColor: theme.palette.brand[600],
            boxShadow: `0 0 0 3px ${alpha(theme.palette.brand[600], 0.06)}`,
          },
        }}
      >
        <Stack spacing={2} sx={{ alignItems: isNewer ? 'flex-start' : 'flex-end' }}>
          {/* "Newer" / "Older", not "Previous" / "Next". On a list sorted newest-first
              those two words are genuinely ambiguous — "next" is the next one DOWN the
              page, which is the older one, and half of readers assume the opposite.
              Direction words that name the axis cannot be read backwards. */}
          <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
            {isNewer && <ArrowLeftIcon size={14} strokeWidth={1.5} />}
            <Typography variant="body4">{isNewer ? 'Newer' : 'Older'}</Typography>
            {!isNewer && <ArrowRightIcon size={14} strokeWidth={1.5} />}
          </Stack>
          <Typography
            variant="body3"
            sx={{
              fontWeight: 600,
              lineHeight: 1.4,
              textAlign: isNewer ? 'left' : 'right',
              // Two lines, then ellipsis. Titles are free text with no cap, and one long
              // title would otherwise make its tile taller than its neighbour — a ragged
              // pair at the foot of the page, which is the opposite of finishing it.
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden',
            }}
          >
            {announcement.title}
          </Typography>
        </Stack>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'grid',
        // Two fixed columns rather than `auto-fit`, so a lone tile keeps its own side
        // instead of stretching across the page and losing the direction it encodes.
        gridTemplateColumns: '1fr 1fr',
        gap: 3,
      }}
    >
      {newer && tile(newer, 'newer')}
      {older && tile(older, 'older')}
    </Box>
  )
}
